<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Config } from '@/Config'
import * as Server from '@/services/Server'
import * as Auth from '@/services/Auth'
import * as Alerts from '@/services/Alerts'
import PageHeader from '@/components/PageHeader.vue'
import ChipPoints from '@/components/ChipPoints.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import EventActivationButton from '@/components/EventActivationButton.vue'
import EventResetButton from '@/components/EventResetButton.vue'
import EventTrackRow from '@/components/EventTrackRow.vue'
import GamePlayButton from '@/components/GamePlayButton.vue'
import UserName from '@/components/UserName.vue'
import TrackPointsTable from '@/components/TrackPointsTable.vue'
import TrackTimesTable from '@/components/TrackTimesTable.vue'
import { formatDayMonth, formatTime } from '@/lib/dates'
import { eventStateColor } from '@/lib/events'
import { podiumClass } from '@/lib/podium'
import { formatGap, gapsToPrevious } from '@/lib/times'
import { useEventTitle } from '@/composables/useEventTitle'
import { useTrackTitle } from '@/composables/useTrackTitle'

const route = useRoute()
const { t } = useI18n()
const eventTitle = useEventTitle()
const { fullName } = useTrackTitle()

const eventId = computed(() => route.params.id)
const event = ref(null)
const loading = ref(true)
const error = ref(false)

const isAuthenticated = Auth.isAuthenticated()

/** When the event closes — or closed — in the timezone the visitor reads the dates in. */
const closing = computed(() => {
  if (!event.value) return ''

  const params = { date: formatDayMonth(event.value.dateEnd), time: formatTime(event.value.dateEnd) }

  return t(event.value.state === 'finished' ? 'events.closed' : 'events.closes', params)
})

/**
 * The name of the event, in the header and in the breadcrumb: an event that has a name of its own
 * ("Championship of September") shows that instead of its type.
 */
const title = computed(() => eventTitle(event.value, `#${eventId.value}`))

const breadcrumbs = computed(() => [
  { title: t('events.breadcrumb'), to: { name: 'events' } },
  { title: event.value ? t(`eventTypes.${event.value.eventType}`) : `#${eventId.value}` },
])

/** The player's result on the given track, when they already have one. */
const myResultFor = (trackId) => event.value?.myResults?.find(r => r.eventTrackId === trackId) ?? null

/**
 * The hardcore stage attempt on the given track, when the player has one that is not finished:
 * `running` while they are driving it, `abandoned` once it cannot go on (see EventStageLib on the
 * server). A finished attempt has a result instead, so the two never both exist.
 */
const myAttemptFor = (trackId) => event.value?.myAttempts?.find(a => a.eventTrackId === trackId) ?? null

/**
 * Whether the track holds the stage the player may erase and race again: only the one they played
 * last (the server names it in `retryTrackId`), with a retry left in the event. A running attempt
 * is not retryable — erasing it would be a free reset, which is the whole point of a hardcore event.
 */
const canRetryStage = (track) => Boolean(event.value?.playable
  && event.value?.hardcore
  && (event.value?.retriesLeft ?? 0) > 0
  && event.value?.retryTrackId === track.id)

const retryTrack = ref(null)
const retryConfirming = ref(false)
const retrying = ref(false)

const retryText = computed(() => {
  const trackName = retryTrack.value ? fullName(retryTrack.value.trackKey) : ''
  return t('events.retryText', { track: trackName, n: event.value?.retriesLeft ?? 0 })
})

const askRetryStage = (track) => {
  retryTrack.value = track
  retryConfirming.value = true
}

/** Spends one retry on that stage: the server erases it and hands back the updated event. */
const retryStage = async () => {
  if (!retryTrack.value) return

  retrying.value = true

  try {
    const response = await Server.del(Config.server.routes.api.events_retry_stage(event.value.id, retryTrack.value.trackKey))
    applyReset(response.data?.data ?? null)
    Alerts.success(t('events.retryDone'))
  } catch (error) {
    Alerts.error(error.response?.data?.message ?? t('events.retryError'))
  } finally {
    retrying.value = false
    retryTrack.value = null
  }
}

/** Who won the event: the first row of the standings, when the event has one. */
const winner = computed(() =>
  event.value?.standings?.find(row => row.position === 1)?.playerName ?? null
)

/**
 * The standings are read one track at a time — where every point was earned, or the gap to the best
 * time of each track — or as the plain list. Both families have the two views (TrackPointsTable and
 * TrackTimesTable) and open on the columns.
 */
const VIEW = { TRACKS: 'tracks', LIST: 'list' }
const standingsView = ref(VIEW.TRACKS)

/** A time event has no points: its track columns show the gap to the best time (see TrackTimesTable). */
const isTimeBased = computed(() => Boolean(event.value) && !event.value.pointsBased && !event.value.season)

/** Track keys of the columns: the tracks of the event, in their order. */
const trackKeys = computed(() => (event.value?.tracks ?? []).map(track => track.trackKey))

/** The standings in the shape the shared table reads (see TrackPointsTable). */
const standingsRows = computed(() => (event.value?.standings ?? []).map(row => ({
  id: row.userId,
  name: row.playerName,
  crest: row.crest,
  position: row.position,
  total: row.points,
  wins: row.wins,
  isMe: row.isMe,
  tracks: row.tracks ?? {},
})))

/**
 * The distance of every standings row from the one above it, in the order the list shows them: the
 * first row has none, being the one the others are measured against, and neither has the one opening
 * a group of players who raced a different number of tracks — those totals are not comparable.
 */
const previousGaps = computed(() =>
  gapsToPrevious(
    event.value?.standings ?? [],
    row => row.resultMs,
    row => row.played,
  ).map(formatGap)
)

/**
 * The standings in the shape the time table reads (see TrackTimesTable): the sum of the times (the
 * gap to the leader rides its tooltip), the distance from the row above it, and one cell per track
 * with its own gap.
 */
const timeRows = computed(() => (event.value?.standings ?? []).map((row, index) => ({
  id: row.userId,
  name: row.playerName,
  crest: row.crest,
  position: row.position,
  total: row.resultTime,
  gap: row.resultGapTime,
  previousGap: previousGaps.value[index],
  isMe: row.isMe,
  tracks: row.tracks ?? {},
})))

/** The columns per track, on the screen and with something to show. */
const showTrackColumns = computed(() => (event.value?.pointsBased || isTimeBased.value)
  && standingsView.value === VIEW.TRACKS
  && (event.value?.standings?.length ?? 0) > 0)

const fetchEvent = () => {
  loading.value = true
  error.value = false

  Server.get(Config.server.routes.api.events_detail(eventId.value))
    .then(response => {
      event.value = response.data?.data ?? null
    })
    .catch(() => {
      error.value = true
      event.value = null
    })
    .finally(() => {
      loading.value = false
    })
}

/**
 * Takes what the reset button hands back: the event as it is now, with the entry gone and the
 * standings recomputed, so there is nothing to ask for again.
 */
const applyReset = (updated) => {
  if (updated) event.value = updated
}

/**
 * Takes the choice the activation button hands back (see components/EventActivationButton.vue):
 * only the flag changes, so the event object stays and keeps everything else the page shows.
 */
const applyActivation = (active) => {
  if (event.value) event.value.myActivation = active
}

onMounted(fetchEvent)
watch(eventId, fetchEvent)
</script>

<template>
  <main>
    <v-container>
      <v-breadcrumbs :items="breadcrumbs" class="px-0 pb-2">
        <template v-slot:divider>
          <v-icon>mdi-chevron-right</v-icon>
        </template>
      </v-breadcrumbs>

      <div v-if="loading" class="d-flex justify-center py-12">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <v-alert v-else-if="error" type="error" variant="tonal">
        {{ $t('events.loadError') }}
      </v-alert>

      <template v-else-if="event">
        <PageHeader :title="title" icon="mdi-calendar-star">
          <template #append>
            <v-chip :color="eventStateColor(event.state)" size="small" variant="tonal" label class="ms-3">
              {{ $t(`eventStates.${event.state}`) }}
            </v-chip>
          </template>
        </PageHeader>

        <p class="d-flex align-center ga-1 text-body-2 text-medium-emphasis mb-4">
          <v-icon size="16">mdi-clock-outline</v-icon>
          <span>{{ closing }}</span>
        </p>

        <v-row>
          <v-col cols="12" md="8" class="order-1">
            <v-card elevation="3" rounded="lg" class="h-100 overflow-hidden">
              <div class="d-flex align-center ga-3 pa-4">
                <v-avatar color="amber-darken-2" variant="tonal" size="48">
                  <v-icon size="28">mdi-trophy-variant</v-icon>
                </v-avatar>
                <div class="flex-grow-1">
                  <h2 class="text-h6 font-weight-bold">{{ $t('events.standings') }}</h2>
                </div>

                <v-btn-toggle
                  v-if="event.pointsBased || isTimeBased"
                  v-model="standingsView"
                  density="compact"
                  variant="tonal"
                  divided
                  mandatory
                >
                  <v-btn
                    :value="VIEW.TRACKS"
                    icon="mdi-view-column-outline"
                    size="small"
                    :title="$t(event.pointsBased ? 'events.viewByTrack' : 'events.viewByTrackTimes')"
                  />
                  <v-btn
                    :value="VIEW.LIST"
                    icon="mdi-table-large"
                    size="small"
                    :title="$t('events.viewByList')"
                  />
                </v-btn-toggle>
              </div>

              <v-divider />

              <!-- Columns per track: the points every player took, or the gap to the best time. -->
              <TrackPointsTable
                v-if="showTrackColumns && event.pointsBased"
                :rows="standingsRows"
                :track-keys="trackKeys"
                :event-id="eventId"
              />

              <TrackTimesTable
                v-else-if="showTrackColumns"
                :rows="timeRows"
                :track-keys="trackKeys"
                :event-id="eventId"
              />

              <v-table v-else-if="event.standings.length" hover>
                <thead>
                  <tr>
                    <th class="text-center" style="width: 56px">#</th>
                    <th>{{ $t('events.player') }}</th>
                    <!-- A points-based event is won on points: the sum of the times is only the last
                         tie-break, so it stays out of the table -->
                    <th v-if="!event.pointsBased" class="text-center">{{ $t('events.result') }}</th>
                    <th v-if="!event.pointsBased" class="text-center">{{ $t('standings.gap') }}</th>
                    <th v-if="event.pointsBased" class="text-center">{{ $t('events.points') }}</th>
                    <!-- For a time event the total is comparable only knowing how many tracks it
                         was made on. -->
                    <th v-if="!event.pointsBased" class="text-center">{{ $t('events.played') }}</th>
                    <th class="text-center">{{ $t('events.wins') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, index) in event.standings"
                    :key="row.userId"
                    :class="{ 'me-row': row.isMe }"
                  >
                    <td class="text-center">
                      <v-icon v-if="row.position <= 3" :class="podiumClass(row.position)" size="20">mdi-medal</v-icon>
                      <span v-else class="text-medium-emphasis">{{ row.position }}</span>
                    </td>
                    <td class="font-weight-medium"><UserName :name="row.playerName" :crest="row.crest" /></td>
                    <td v-if="!event.pointsBased" class="text-center text-mono">{{ row.resultTime ?? '–' }}</td>
                    <td v-if="!event.pointsBased" class="text-center text-mono text-medium-emphasis">
                      {{ previousGaps[index] ?? '' }}
                    </td>
                    <td v-if="event.pointsBased" class="text-center font-weight-bold">
                      {{ row.points ?? '–' }}
                    </td>
                    <td v-if="!event.pointsBased" class="text-center">{{ row.played }}</td>
                    <td class="text-center">
                      <span v-if="row.wins">{{ row.wins }}</span>
                      <span v-else class="text-medium-emphasis">–</span>
                    </td>
                  </tr>
                </tbody>
              </v-table>

              <div v-else class="d-flex align-center ga-2 pa-4 text-medium-emphasis">
                <v-icon size="20">mdi-trophy-outline</v-icon>
                <span class="text-caption">{{ $t('events.noStandings') }}</span>
              </div>

              <!-- Legend of the standings, under the table it refers to -->
              <p
                v-if="event.standings.length"
                class="text-caption text-medium-emphasis px-4 pb-4 pt-2 mb-0 mt-2"
              >
                <template v-if="event.pointsBased">
                  {{ $t('events.pointsLegend') }}:
                  {{ event.pointsPerPosition.map((points, index) => `${index + 1}° ${points}`).join(' · ') }}
                </template>
                <template v-else>{{ $t('events.timeBasedLegend') }}</template>
              </p>
            </v-card>
          </v-col>

          <!-- The player's side of the event: what to do, and the tracks to do it on -->
          <v-col cols="12" md="4" class="order-2 d-flex flex-column ga-4">
            <v-card elevation="3" rounded="lg" class="d-flex flex-column pa-4">
              <v-btn
                v-if="!isAuthenticated"
                :to="{ name: 'login' }"
                color="primary"
                size="large"
                class="w-100"
              >
                {{ $t('events.loginToParticipate') }}
              </v-btn>
              <GamePlayButton
                v-else-if="event.playable"
                :label="$t('play.playNow')"
                size="large"
                class="w-100"
              />
              <!-- An event that is over has nothing left to do: a line of text says who won it and
                   takes far less room than a dead button -->
              <div
                v-else-if="event.state === 'finished'"
                class="text-center text-body-2 text-medium-emphasis py-2"
              >
                <template v-if="winner">
                  <v-icon size="18" class="mr-1">mdi-trophy-variant</v-icon>
                  {{ $t('events.winner') }}: <UserName :name="winner" :show-crest="false" class="font-weight-medium" />
                </template>
                <!-- Finished without a single result: there is no winner to name -->
                <template v-else>
                  <v-icon size="18" class="mr-1">mdi-flag-checkered</v-icon>
                  {{ $t('events.finished') }}
                </template>
              </div>
              <v-btn v-else disabled size="large" class="w-100">
                {{ $t('events.notPlayable') }}
              </v-btn>
              <!-- The gate of an event that asks for an explicit activation (a stage): right
                   under the play button, and only where the component has something to offer. -->
              <EventActivationButton
                :event="event"
                class="mt-2"
                @change="applyActivation"
              />
              <!-- Outside the chain above, and under the play button: the second chance of a
                   one-attempt type. The component decides whether it has anything to offer (see
                   components/EventResetButton.vue). -->
              <EventResetButton :event="event" class="mt-2" @reset="applyReset" />

              <!-- The player's own result: the one number worth keeping beside the button -->
              <template v-if="isAuthenticated">
                <v-divider class="my-4" />

                <div class="d-flex align-center ga-2">
                  <v-icon color="primary" size="20">mdi-account-check</v-icon>
                  <span class="text-body-2 font-weight-medium">{{ $t('events.yourResult') }}</span>
                  <v-spacer />
                  <template v-if="event.myEntry">
                    <v-chip color="primary" size="small" variant="flat" label>
                      {{ $t('events.position') }} {{ event.myEntry.position }}
                    </v-chip>
                    <ChipPoints v-if="event.myEntry.points !== null" :points="event.myEntry.points" />
                    <v-chip v-else-if="event.myEntry.resultTime" size="small" variant="tonal" label>
                      {{ event.myEntry.resultTime }}
                    </v-chip>
                  </template>
                  <span v-else class="text-body-2 text-medium-emphasis">–</span>
                </div>
              </template>
            </v-card>

            <!-- The tracks of the event: the second half of what concerns the player -->
            <v-card id="event-tracks" elevation="3" rounded="lg" class="overflow-hidden">
              <div class="d-flex align-center ga-2 px-3 py-2">
                <v-icon size="20" color="primary">mdi-flag-checkered</v-icon>
                <h2 class="text-subtitle-1 font-weight-bold flex-grow-1">{{ $t('events.tracks') }}</h2>
              </div>

              <!-- A hardcore event spends a stage as soon as it starts: the retries are the
                   only way to race one of them again (see canRetryStage above). -->
              <p
                v-if="event.hardcore && event.playable && isAuthenticated"
                class="text-caption text-medium-emphasis px-3 pb-2 mb-0"
              >
                {{ $t('events.retriesLeft', { n: event.retriesLeft }) }}
              </p>

              <v-divider />

              <EventTrackRow
                v-for="track in event.tracks"
                :key="track.id"
                :track="track"
                :result="myResultFor(track.id)"
                :attempt="myAttemptFor(track.id)"
                :can-retry="canRetryStage(track)"
                :retrying="retrying && retryTrack?.id === track.id"
                :event-id="event.id"
                @retry="askRetryStage(track)"
              />
            </v-card>
          </v-col>
        </v-row>

        <!-- What the event asks of a player: read once, so it sits at the foot of the page -->
        <v-card elevation="3" rounded="lg" class="mt-6 mb-6 overflow-hidden">
          <div class="d-flex align-center ga-3 pa-4">
            <v-avatar color="primary" variant="tonal" size="48">
              <v-icon size="28">mdi-information-outline</v-icon>
            </v-avatar>
            <h2 class="text-h6 font-weight-bold">{{ $t('events.howItWorks') }}</h2>
          </div>

          <v-divider />

          <p class="text-body-2 px-4 py-4 mb-0 event-rules">{{ $t(`events.rules.${event.eventType}`) }}</p>
        </v-card>
      </template>
    </v-container>

    <!-- Outside the loaded-state block: the dialog has to stay mounted while it closes. -->
    <ConfirmDialog
      v-model="retryConfirming"
      :title="$t('events.retryTitle')"
      :text="retryText"
      :confirm-text="$t('events.retryConfirm')"
      :cancel-text="$t('common.cancel')"
      confirm-color="warning"
      @confirm="retryStage"
    />
  </main>
</template>

<style scoped>
.me-row td {
  background-color: rgba(var(--v-theme-primary), 0.06);
}

/* The rules are written as paragraphs in the lang files (\n): the breaks have to show up. */
.event-rules {
  white-space: pre-line;
}
</style>
